import { forwardRef, useEffect, useImperativeHandle, useState, useRef } from 'react';
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

export default forwardRef((props, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const containerRef = useRef(null);

    const selectItem = (index) => {
        const item = props.items[index];
        if (item) {
            props.command({ id: item.userId, label: item.userName, avatarColor: item.avatarColor });
        }
    };

    const upHandler = () => {
        setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
    };

    const downHandler = () => {
        setSelectedIndex((selectedIndex + 1) % props.items.length);
    };

    const enterHandler = () => {
        selectItem(selectedIndex);
    };

    useEffect(() => setSelectedIndex(0), [props.items]);

    useEffect(() => {
        const updatePosition = () => {
            if (containerRef.current) {
                // Remove the previously set transform to get natural position
                containerRef.current.style.transform = '';
                
                const rect = containerRef.current.getBoundingClientRect();
                const menuHeight = rect.height;
                const spaceBelow = window.innerHeight - rect.top;
                const spaceAbove = rect.top;
                const shouldShowAbove = spaceBelow < menuHeight + 16 && spaceAbove > spaceBelow;
                
                let yOffset = shouldShowAbove ? -menuHeight - 24 : 8;

                containerRef.current.style.transform = `translateY(${yOffset}px)`;
            }
        };

        updatePosition();
        const timeoutId = setTimeout(updatePosition, 0);

        // Add event listeners for scroll and resize
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [props.items]);

    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }) => {
            if (event.key === 'ArrowUp') {
                upHandler();
                return true;
            }

            if (event.key === 'ArrowDown') {
                downHandler();
                return true;
            }

            if (event.key === 'Enter') {
                enterHandler();
                return true;
            }

            return false;
        },
    }));

    if (!props.items.length) {
        return null;
    }

    return (
        <div ref={containerRef} className="fixed z-50">
            <div className="bg-background border rounded-md shadow-md overflow-hidden">
                {props.items.map((item, index) => (
                    <button
                        className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-muted ${
                            index === selectedIndex ? 'bg-muted' : ''
                        }`}
                        key={index}
                        onClick={() => selectItem(index)}
                    >
                        <div className="flex items-center gap-2">
                            <Avatar className="h-5 w-5">
                                <AvatarFallback className={`${item.avatarColor || 'bg-neutral-600'} text-neutral-200 text-[0.65rem]`}>
                                    {item.initials}
                                </AvatarFallback>
                            </Avatar>
                            {item.userName}
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}); 